import React, { useState, useEffect } from 'react';
import { Layout, Row, Col } from 'antd';
import ReportLayout from '../ReportLayout';
import FilterDrawer from 'utils/commonFilter/FilterDrawer';
import ListingTable from './ListingTable';
import { useLocation } from 'react-router';
import CreateScheduler from 'utils/commonScheduler/createScheduler';
import { RBAC, useRbac } from 'features/rbac/rbac';
import { ERbacPermissions } from 'features/rbac/rbacPermissionsList';
import { IFilterOptions, IReportQueryString } from 'models/interfaces';
import { usePageTitle } from 'hooks';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { actions, dateRange } from 'models/Slices/FilterSlice';
import {
  decodeQueryStringData,
  urlQueryParamsToObject,
} from 'utils/commonFunction';
import { dataSource } from 'models/Slices/GlobalSlice';

const Listing: React.FC = () => {
  const dispatch = useAppDispatch();
  const DataSource = useAppSelector(dataSource);
  const dates = useAppSelector(dateRange);
  const { hasPermissions } = useRbac();

  useEffect(() => {
    dates.length <= 0 &&
      dispatch(actions.getDateRangeFilterData({ env: DataSource }));

    dispatch(actions.getRecipientFilterData({ env: DataSource }));

    if (hasPermissions([ERbacPermissions.FILTER_BROKER_USER_SELECTION_ALL])) {
      dispatch(actions.getBrokerUserData({ env: DataSource }));
    }

    if (
      hasPermissions([
        ERbacPermissions.FILTER_LISTER_USER_SELECTION_ALL,
        ERbacPermissions.FILTER_LISTER_USER_SELECTION_TEAM,
      ])
    ) {
      dispatch(actions.getListerUserData({ env: DataSource }));
    }
  }, [dispatch]);

  usePageTitle('Listing Report');
  const location = useLocation();
  const queryObj = urlQueryParamsToObject(location.search);
  const reportQueryString: IReportQueryString = decodeQueryStringData(
    queryObj.report,
  );

  const [filterObject, setFilterObject] = useState<IFilterOptions | null>(null);
  const [filtersAppliedWithValues, setFilterAppliedWithValues] =
    useState<any>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [ifShowFilterDrawer, setIfShowFilterDrawer] = useState(false);
  const [filterQueryString, setFilterQueryString] = useState<any>(null);
  const [defaultDateRange, setDefaultDateRange] = useState<any>(null);
  const [outerLoader, setOuterLoader] = useState(false);

  useEffect(() => {
    setOuterLoader(true);
    if (queryObj.hasOwnProperty('filter')) {
      setFilterQueryString(decodeQueryStringData(queryObj.filter));
    }
  }, []);

  useEffect(() => {
    if (dates.length > 0) {
      setDefaultDateRange({
        preFilledValues: {
          po_date_from: filterQueryString?.po_date_from,
          po_date_to: filterQueryString?.po_date_to,
          invoice_date_from: filterQueryString?.invoice_date_from,
          invoice_date_to: filterQueryString?.invoice_date_to,
          event_date_from: filterQueryString?.event_date_from,
          event_date_to: filterQueryString?.event_date_to,
        },
      });
      setIfShowFilterDrawer(true);
    }
  }, [dates, filterQueryString]);

  const filterChanged = (filters: any) => {
    setFilterObject({
      ...filters,
    });
  };

  const reorderAppliedFilters = (filters: any) => {
    if (!Array.isArray(filters)) {
      return filters;
    }
    const invoiceIndex = filters.findIndex(
      (f: any) => f.field_id === 'invoice_date_range',
    );
    const eventIndex = filters.findIndex(
      (f: any) => f.field_id === 'event_date_range',
    );
    if (invoiceIndex === -1 || eventIndex === -1 || invoiceIndex < eventIndex) {
      return filters;
    }
    const reordered = [...filters];
    const [invoiceEntry] = reordered.splice(invoiceIndex, 1);
    const newEventIndex = reordered.findIndex(
      (f: any) => f.field_id === 'event_date_range',
    );
    reordered.splice(newEventIndex, 0, invoiceEntry);
    return reordered;
  };

  const handleSearch = (e: any) => {
    e.preventDefault();
    setSearchQuery(search);
  };

  const closeSearch = () => {
    setSearch('');
    setSearchQuery('');
  };

  return (
    <React.Fragment>
      <div className="mainHeading">
        <Row className="flx-1">
          <Col xs={24} xl={9} xxl={9} className="gpReportTitle">
            <h2>{reportQueryString.current_report}</h2>
          </Col>
          <Col xs={16} xl={8} xxl={7}>
            <div className="search">
              <form onSubmit={handleSearch}>
                <input
                  type="search"
                  placeholder="Search invoice id, purchase id, or event name..."
                  value={search ? search : ''}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && search.length > 0 && (
                  <span onClick={closeSearch}>&times;</span>
                )}
              </form>
            </div>
          </Col>
          <Col xs={8} xl={7} xxl={8}>
            <div className="rightFilter">
              <RBAC
                allowedPermissions={[ERbacPermissions.REPORT_LISTING_SCHEDULE]}
              >
                <CreateScheduler
                  schedulerTitle={'Create Schedule'}
                  reportData={reportQueryString}
                  filterData={filterObject}
                />
              </RBAC>
              {ifShowFilterDrawer && (
                <FilterDrawer
                  current_report_id={reportQueryString.current_report_id}
                  showPODatePicker={true}
                  showInvoiceDatePicker={true}
                  showEventDatePicker={true}
                  showBrokerUserDropdown={hasPermissions([
                    ERbacPermissions.FILTER_BROKER_USER_SELECTION_ALL,
                  ])}
                  showListerDropdown={hasPermissions([
                    ERbacPermissions.FILTER_LISTER_USER_SELECTION_ALL,
                    ERbacPermissions.FILTER_LISTER_USER_SELECTION_TEAM,
                  ])}
                  showEventNameDropdown={hasPermissions([
                    ERbacPermissions.FILTER_EVENT_NAME_ALL,
                  ])}
                  showTbPlacementCheckbox={true}
                  showIncludeOnlyListingsCheckbox={true}
                  onApplyFilter={filterChanged}
                  onApplyFilterWithValues={(e: any) =>
                    setFilterAppliedWithValues(reorderAppliedFilters(e))
                  }
                  brokerUserDropdownType="single"
                  listerDropdownType="single"
                  eventNameDropdownType="single"
                  defaultAndValues={defaultDateRange}
                />
              )}
            </div>
          </Col>
        </Row>
      </div>
      <ReportLayout>
        <Layout className="mainInnerLayout pb-0 reduceTopSpace">
          <ListingTable
            filterObject={filterObject}
            appliedFilterValues={filtersAppliedWithValues}
            searchQuery={searchQuery}
            outerLoader={outerLoader}
            setOuterLoader={setOuterLoader}
          />
        </Layout>
      </ReportLayout>
    </React.Fragment>
  );
};

export default Listing;
